import { useCallback, useSyncExternalStore } from 'react';
import {
  selectGroupGuid,
  selectUserGuid,
  useAppSelector,
} from 'slices/commonSelectors';
import {
  getReadIdsSnapshot,
  markRead,
  readStorageKey,
  subscribeNotificationReadState,
  type NotificationReadIds,
} from './notificationReadState';

const EMPTY_READ_IDS: NotificationReadIds = {};

export function useNotificationReadState() {
  const userGuid = useAppSelector(selectUserGuid);
  const groupGuid = useAppSelector(selectGroupGuid);
  const key = userGuid && groupGuid ? readStorageKey(userGuid, groupGuid) : '';

  const readIds = useSyncExternalStore(
    subscribeNotificationReadState,
    () => getReadIdsSnapshot(key),
    () => EMPTY_READ_IDS
  );

  const markIdsRead = useCallback(
    (ids: readonly string[], inboxIds?: Iterable<string>) => {
      if (!key || ids.length === 0) return;
      markRead(key, ids, { inboxIds });
    },
    [key]
  );

  const isRead = useCallback(
    (notificationId: string) => notificationId in readIds,
    [readIds]
  );

  const markAllRead = useCallback(
    (ids: readonly string[], inboxIds?: Iterable<string>) => {
      markIdsRead(ids, inboxIds);
    },
    [markIdsRead]
  );

  return { readIds, isRead, markRead: markIdsRead, markAllRead };
}
